import { CircleAlert } from 'lucide-react';
import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useId,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cx } from '../lib/cx.js';
import './field.css';

export interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** Inside a <Field>, gives the control its id, aria-describedby and invalid state. */
export function useField(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  label: ReactNode;
  /** Help text under the control. */
  hint?: ReactNode;
  /** Error message; marks the control invalid and is announced via aria-describedby. */
  error?: ReactNode;
  required?: boolean;
  /** Visually hide the label (it stays the accessible name). */
  hideLabel?: boolean;
  className?: string;
  children: ReactElement | ReactNode;
}

/**
 * Label + control + hint/error wiring. Controls from this package read the
 * field context; any other single child element receives id and aria props.
 */
export function Field({
  label,
  hint,
  error,
  required = false,
  hideLabel = false,
  className,
  children,
}: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const ctx: FieldContextValue = { id, describedBy, invalid: Boolean(error), required };
  return (
    <FieldContext.Provider value={ctx}>
      <div className={cx('vel-field', className)}>
        <label htmlFor={id} className={cx('vel-field__label', hideLabel && 'vel-visually-hidden')}>
          {label}
          {required ? (
            <span className="vel-field__required" aria-hidden>
              *
            </span>
          ) : null}
        </label>
        {isValidElement<Record<string, unknown>>(children) && typeof children.type === 'string'
          ? cloneElement(children, {
              id,
              'aria-describedby': describedBy,
              'aria-invalid': error ? true : undefined,
              'aria-required': required || undefined,
            })
          : children}
        {error ? (
          <span id={errorId} className="vel-field__error">
            <CircleAlert size={12} aria-hidden />
            {error}
          </span>
        ) : null}
        {hint ? (
          <span id={hintId} className="vel-field__hint">
            {hint}
          </span>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
